import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Project } from "../types";
import { Github, ExternalLink, FolderGit2 } from "lucide-react";
import { useLocale } from "../context/LocaleContext";
import { useRouter } from "../context/RouterContext";
import api from "../services/api";

interface ProjectsPageData {
  title: string;
  subtitle: string;
  description: string;
  noProjectsMessage: string;
}

export function ProjectsPage() {
  const { locale } = useLocale();
  const { navigate } = useRouter();
  const [projects, setProjects] = useState<Project[]>([]);
  const [pageData, setPageData] = useState<ProjectsPageData>({
    title: "DevOps",
    subtitle: "Projects",
    description:
      "Real-world DevOps projects, infrastructure setups, and automation examples. Learn by seeing how theory translates into practice.",
    noProjectsMessage: "No projects available yet.",
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      try {
        // Fetch page content
        const pageResponse = await api.getProjectsPage(locale);
        if (pageResponse?.data?.attributes) {
          setPageData(pageResponse.data.attributes);
        } else if (pageResponse?.data) {
          setPageData(pageResponse.data);
        }

        // Fetch projects
        const projectsResponse = await api.getProjects(locale);
        //console.log("Projects response:", projectsResponse);
        if (projectsResponse?.data) {
          const projectsData = Array.isArray(projectsResponse.data)
            ? projectsResponse.data.map((item: any) => ({
                id: item.id,
                name: item.name,
                slug: item.slug,
                description: item.description,
                tech_stack: item.tech_stack || [],
                github_url: item.github_url,
                demo_url: item.demo_url,
                image_url: item.thumbnail?.url || null,
                created_at: item.createdAt,
              }))
            : [];
          //console.log("Processed projects:", projectsData);
          setProjects(projectsData);
        }
      } catch (error) {
        console.error("Failed to fetch projects data:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [locale]);

  return (
    <div className="min-h-screen bg-black py-24 px-4">
      <div className="container mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {pageData.title}{" "}
            <span className="text-red-600">{pageData.subtitle}</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            {pageData.description}
          </p>
        </motion.div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : projects.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20"
          >
            <FolderGit2 className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">
              {pageData.noProjectsMessage}
            </p>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project, idx) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                whileHover={{ y: -5 }}
                onClick={() => navigate("project", { slug: project.slug })}
                className="bg-gray-900/50 backdrop-blur-sm border border-red-900/20 rounded-xl overflow-hidden hover:border-red-600/50 transition-all group cursor-pointer"
              >
                {project.image_url && (
                  <div className="h-48 bg-gray-800 overflow-hidden">
                    <img
                      src={`${
                        process.env.REACT_APP_STRAPI_URL ||
                        "https://devops.caglayagmur.dev/api"
                      }${project.image_url}`}
                      alt={project.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                )}

                <div className="p-6">
                  <h3 className="text-xl font-bold mb-3 group-hover:text-red-600 transition-colors">
                    {project.name}
                  </h3>

                  <p className="text-gray-400 mb-4 line-clamp-3">
                    {project.description}
                  </p>

                  {project.tech_stack && project.tech_stack.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {project.tech_stack.map((tech, techIdx) => (
                        <span
                          key={techIdx}
                          className="px-3 py-1 bg-red-900/20 text-red-400 rounded-full text-sm"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="flex gap-3">
                    {project.github_url && (
                      <motion.a
                        href={project.github_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm transition-colors"
                      >
                        <Github className="w-4 h-4" />
                        Code
                      </motion.a>
                    )}

                    {project.demo_url && (
                      <motion.a
                        href={project.demo_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-sm transition-colors"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Demo
                      </motion.a>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
